import axios, { AxiosError } from 'axios'

const API_BASE_URL = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:5003/api/v1'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config

    // Don't try to refresh tokens for login/auth requests
    const isAuthRequest = originalRequest?.url?.includes('/auth/login') ||
                         originalRequest?.url?.includes('/auth/signup') ||
                         originalRequest?.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry && !isAuthRequest) {
      (originalRequest as any)._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) {
          console.warn('No refresh token available, redirecting to login')
          throw new Error('No refresh token available')
        }

        // Validate refresh token format before sending
        if (typeof refreshToken !== 'string' || refreshToken.split('.').length !== 3) {
          console.warn('Invalid refresh token format, clearing and redirecting to login')
          localStorage.removeItem('refreshToken')
          throw new Error('Invalid refresh token format')
        }

        // Make refresh request directly to avoid circular import
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken
        }, {
          headers: {
            'Content-Type': 'application/json',
          }
        })

        const { accessToken, refreshToken: newRefreshToken } = response.data.data
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        // Retry the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
        }
        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        // Refresh failed, clear tokens and let the error propagate
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')

        // Don't refresh the page, let the app handle navigation
        // The AuthContext will detect missing tokens and redirect appropriately
        return Promise.reject(error) // Return original error, not refresh error
      }
    }

    return Promise.reject(error)
  }
)
